import React, { useState } from "react";
import { Lock, Unlock, KeyRound, ShieldCheck } from "lucide-react";
import VHSTape from "./VHSTape";
import { vaultTapes, vaultPasscode, vaultHint } from "../mock/enrich";
import { useToast } from "../hooks/use-toast";

const VaultSection = ({ onSelectTape }) => {
  const [unlocked, setUnlocked] = useState(false);
  const [value, setValue] = useState("");
  const [error, setError] = useState(false);
  const { toast } = useToast();

  const submit = (e) => {
    e.preventDefault();
    if (value.trim().toLowerCase() === vaultPasscode.toLowerCase()) {
      setUnlocked(true);
      setError(false);
      toast({
        title: "Vault unlocked ●",
        description: "Welcome to the private shelf. Please handle with care.",
      });
    } else {
      setError(true);
      toast({
        title: "Wrong passcode",
        description: vaultHint,
      });
    }
  };

  return (
    <section
      id="vault"
      className="relative py-24 md:py-32 border-t border-[#1f1a15]"
      style={{
        background:
          "radial-gradient(1200px 500px at 50% 0%, #1a0a08 0%, #0e0b09 60%, #0e0b09 100%)",
      }}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div className="reveal">
            <div className="text-[11px] mono uppercase tracking-[0.3em] text-[#e4572e]">
              ▶ The Vault / Client-Only Shelf
            </div>
            <h2
              className="mt-3 text-5xl md:text-6xl leading-[0.95]"
              style={{ fontFamily: "'Instrument Serif', serif" }}
            >
              Unreleased. <br />
              <em className="not-italic text-[#e4572e]">Under embargo.</em>
            </h2>
          </div>
          <div className="reveal flex items-center gap-2 text-[11px] mono text-[#f2ece1]/60">
            {unlocked ? (
              <>
                <Unlock className="w-3.5 h-3.5 text-[#f5b14e]" /> ACCESS GRANTED
              </>
            ) : (
              <>
                <Lock className="w-3.5 h-3.5" /> {vaultTapes.length.toString().padStart(2, "0")} confidential tapes
              </>
            )}
          </div>
        </div>

        {/* Locked keypad */}
        {!unlocked && (
          <div className="reveal max-w-xl mx-auto text-center">
            <div className="inline-flex items-center gap-3 text-[11px] mono uppercase tracking-widest text-[#f5b14e] mb-4">
              <ShieldCheck className="w-4 h-4" /> Passcode Required
            </div>
            <p className="text-[#f2ece1]/75 leading-relaxed">
              This shelf holds work under NDA, unreleased cuts, and pieces still in
              post. Enter the shared passcode to preview — or reach out for a private
              link.
            </p>

            <form
              onSubmit={submit}
              className={`mt-8 flex items-stretch gap-2 rounded-full border p-1.5 transition-colors ${
                error ? "border-[#e4572e]" : "border-[#2a221b] focus-within:border-[#f5b14e]"
              }`}
              style={{ background: "#120e0a" }}
            >
              <div className="flex items-center pl-4 text-[#f2ece1]/50">
                <KeyRound className="w-4 h-4" />
              </div>
              <input
                type="password"
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  setError(false);
                }}
                placeholder="Enter passcode"
                className="flex-1 bg-transparent outline-none text-[#f2ece1] placeholder:text-[#f2ece1]/40 text-sm mono tracking-widest px-3"
                autoComplete="off"
              />
              <button
                type="submit"
                className="h-9 px-5 rounded-full bg-[#f2ece1] text-[#0e0b09] text-[13px] font-medium hover:bg-[#f5b14e] transition-colors"
              >
                Unlock
              </button>
            </form>

            <div className="mt-4 text-[11px] mono text-[#f2ece1]/45">
              {vaultHint}
            </div>

            {/* Locked shelf preview — blurred */}
            <div className="mt-14 shelf-perspective relative">
              <div className="tape-track flex items-end justify-center gap-3 md:gap-4 px-4 min-h-[280px] md:min-h-[340px]"
                   style={{ filter: "blur(2px) brightness(0.7)" }}>
                {vaultTapes.map((t, i) => (
                  <VHSTape key={t.id} tape={t} index={i} total={vaultTapes.length} onClick={() => {}} locked />
                ))}
              </div>
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="flex items-center gap-2 text-[11px] mono uppercase tracking-widest bg-[#0e0b09]/70 border border-[#2a221b] rounded-full px-4 py-2 backdrop-blur-sm">
                  <Lock className="w-3.5 h-3.5 text-[#e4572e]" /> Contents hidden
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Unlocked shelf */}
        {unlocked && (
          <div>
            <div className="shelf-perspective">
              <div className="tape-track flex items-end justify-center gap-3 md:gap-4 px-4 min-h-[300px] md:min-h-[380px]">
                {vaultTapes.map((t, i) => (
                  <VHSTape
                    key={t.id}
                    tape={t}
                    index={i}
                    total={vaultTapes.length}
                    onClick={() => onSelectTape(t)}
                  />
                ))}
              </div>
              <div className="mx-auto max-w-[1200px]">
                <div
                  className="h-[10px] rounded-[2px]"
                  style={{
                    background:
                      "linear-gradient(180deg, #3a2a1f 0%, #1c130d 40%, #0e0b09 100%)",
                    boxShadow:
                      "0 20px 60px -20px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.06)",
                  }}
                />
              </div>
            </div>
            <div className="mt-8 text-center text-[11px] mono text-[#f2ece1]/50">
              Handle with care. Share passcode responsibly.
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default VaultSection;
